import { useCallback, useEffect, useRef, useState } from 'react';

export const MAX_RECORDING_SECONDS = 120;

/** Safari first (mp4/AAC), then Chrome/Firefox (webm/opus), then anything. */
const PREFERRED_MIME_TYPES = [
  'audio/mp4;codecs=mp4a.40.2',
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
] as const;

export type VoiceRecorderError = 'mic-denied' | 'recorder-unavailable';

function pickMimeType(): string | undefined {
  if (typeof MediaRecorder === 'undefined') {
    return undefined;
  }
  return PREFERRED_MIME_TYPES.find((type) => MediaRecorder.isTypeSupported(type));
}

/**
 * Tap-to-talk recorder shared by the platform assistant composers: `start()`
 * begins a `MediaRecorder` capture, `stop()` resolves with the recorded blob,
 * `cancel()` discards the take. Stops by itself at `MAX_RECORDING_SECONDS`.
 * Permission denial surfaces as `error: 'mic-denied'` instead of an exception.
 */
export function useVoiceRecorder() {
  const [isRecording, setIsRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState<VoiceRecorderError | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const teardown = useCallback(() => {
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    streamRef.current?.getTracks().forEach((track) => {
      track.stop();
    });
    streamRef.current = null;
    recorderRef.current = null;
    setIsRecording(false);
  }, []);

  useEffect(() => teardown, [teardown]);

  const stop = useCallback((): Promise<Blob> => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state === 'inactive') {
      teardown();
      return Promise.resolve(new Blob([], { type: 'audio/webm' }));
    }
    return new Promise<Blob>((resolve) => {
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' });
        teardown();
        resolve(blob);
      };
      recorder.stop();
    });
  }, [teardown]);

  /** Discard the take: no blob is ever produced, the stream is released. */
  const cancel = useCallback(() => {
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      // Detach onstop so the discard can't resolve a pending stop() promise.
      recorder.onstop = null;
      recorder.stop();
    }
    chunksRef.current = [];
    teardown();
  }, [teardown]);

  const start = useCallback(async (): Promise<void> => {
    setError(null);
    const mimeType = pickMimeType();
    if (!mimeType) {
      setError('recorder-unavailable');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      const recorder = new MediaRecorder(stream, { mimeType });
      recorderRef.current = recorder;
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };
      setSeconds(0);
      setIsRecording(true);
      recorder.start(250);
      const startedAt = Date.now();
      timerRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startedAt) / 1000);
        setSeconds(elapsed);
        if (elapsed >= MAX_RECORDING_SECONDS) {
          void stop();
        }
      }, 1000);
    } catch {
      teardown();
      setError('mic-denied');
    }
  }, [stop, teardown]);

  return { isRecording, seconds, error, start, stop, cancel };
}
